import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Language, PaymentMethod, User } from '@teo/shared';
import { api } from '../api/client';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { DataTable } from '../components/DataTable';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { VIEW_PERMISSION, type ViewKey } from '../nav/navModel';

interface UserForm {
  id: number | null;
  username: string;
  email: string;
  full_name: string;
  password: string;
  role_code: string;
  location_ids: number[];
  active: boolean;
  preferred_language: Language;
  /** Módulos habilitados: parten del rol y Gerencia puede marcarlos o desmarcarlos. */
  permissions: string[];
}

const KINDS = ['CASH', 'TRANSFER', 'CARD', 'DIRECT_CREDIT', 'FINANCIER'] as const;
/** Módulos que se pueden habilitar o quitar por usuario, en el orden del menú. */
const MODULES: ViewKey[] = ['dashboard', 'warehouse', 'warehouseSales', 'inventory', 'supply', 'products', 'cleanup', 'stores', 'cash', 'credits',
  'audit', 'reports', 'purchaseAuth', 'purchaseRequests', 'movements', 'config', 'pos', 'receiveStore', 'masterEntry', 'masterDispatch'];

/** Vista "Configuración": usuarios, medios de pago y financiadores, tipos de movimiento. */
export function Settings() {
  const { t } = useTranslation();
  const { show, showError } = useToast();
  const users = useLoad(() => api.users());
  const roles = useLoad(() => api.roles());
  const locations = useLoad(() => api.locations());
  const methods = useLoad(() => api.paymentMethods());
  const movementTypes = useLoad(() => api.movementTypes());
  const { busy, run } = useSubmit();

  const [userForm, setUserForm] = useState<UserForm | null>(null);
  const [resetFor, setResetFor] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [pm, setPm] = useState({ code: '', name: '', kind: 'FINANCIER' as (typeof KINDS)[number] });

  const locationName = useMemo(() => new Map((locations.data ?? []).map((l) => [l.id, l.name])), [locations.data]);
  const roleScoped = (code: string) => roles.data?.find((r) => r.code === code)?.scoped ?? true;

  const openUser = (u?: User) =>
    setUserForm(
      u
        ? { id: u.id, username: u.username, email: u.email ?? '', full_name: u.full_name, password: '', role_code: u.role_code, location_ids: u.location_ids, active: u.active, preferred_language: u.preferred_language, permissions: u.permissions }
        : { id: null, username: '', email: '', full_name: '', password: '', role_code: 'store', location_ids: [], active: true, preferred_language: 'es', permissions: [] },
    );

  /** Marcar o desmarcar un módulo mueve todos los permisos que lo abren. */
  const toggleModule = (codigos: string[]) =>
    setUserForm((f) => {
      if (!f) return f;
      const tenia = codigos.some((c) => f.permissions.includes(c));
      return { ...f, permissions: tenia ? f.permissions.filter((p) => !codigos.includes(p)) : [...f.permissions, ...codigos] };
    });

  const submitUser = async (e: FormEvent) => {
    e.preventDefault();
    if (!userForm) return;
    const scope = userForm.id ? `usuario:${userForm.id}` : 'usuario:nuevo';
    await run(async () => {
      try {
        const key = requestKey(scope);
        if (userForm.id) {
          await api.updateUser(key, userForm.id, { email: userForm.email.trim() || null, full_name: userForm.full_name, role_code: userForm.role_code, location_ids: userForm.location_ids, active: userForm.active });
          // Los módulos se guardan aparte: el rol define el punto de partida y aquí van las excepciones.
          await api.setUserPermissions(requestKey(`${scope}:modulos`), userForm.id, userForm.permissions);
          releaseKey(`${scope}:modulos`);
        } else {
          await api.createUser(key, {
            username: userForm.username, email: userForm.email.trim() || null, full_name: userForm.full_name, password: userForm.password, role_code: userForm.role_code,
            location_ids: userForm.location_ids, preferred_language: userForm.preferred_language,
          });
        }
        releaseKey(scope);
        setUserForm(null);
        show(t('settings.users.saved'));
        await users.reload();
      } catch (err) {
        showError(err);
      }
    });
  };

  const submitReset = async (e: FormEvent) => {
    e.preventDefault();
    if (!resetFor) return;
    const scope = `usuario:${resetFor.id}:clave`;
    await run(async () => {
      try {
        await api.resetPassword(requestKey(scope), resetFor.id, newPassword);
        releaseKey(scope);
        setResetFor(null);
        setNewPassword('');
        show(t('settings.users.passwordSaved'));
        await users.reload();
      } catch (err) {
        showError(err);
      }
    });
  };

  const submitPm = async (e: FormEvent) => {
    e.preventDefault();
    await run(async () => {
      try {
        await api.createPaymentMethod(requestKey('medio:nuevo'), { code: pm.code, name: pm.name, kind: pm.kind, active: true, sort_order: 100 });
        releaseKey('medio:nuevo');
        setPm({ code: '', name: '', kind: 'FINANCIER' });
        show(t('settings.payments.saved'));
        await methods.reload();
      } catch (err) {
        showError(err);
      }
    });
  };

  const togglePm = async (m: PaymentMethod) => {
    const scope = `medio:${m.id}:${m.active ? 'off' : 'on'}`;
    await run(async () => {
      try {
        await api.updatePaymentMethod(requestKey(scope), m.id, { name: m.name, active: !m.active, sort_order: m.sort_order });
        releaseKey(scope);
        await methods.reload();
      } catch (err) {
        showError(err);
      }
    });
  };

  const toggleLocation = (id: number) => {
    if (!userForm) return;
    const has = userForm.location_ids.includes(id);
    setUserForm({ ...userForm, location_ids: has ? userForm.location_ids.filter((x) => x !== id) : [...userForm.location_ids, id] });
  };

  return (
    <>
      <div className="g2">
        <Panel
          title={t('settings.users.title')}
          description={t('settings.users.description')}
          badge={
            <button type="button" className="btn black" id="addUser" onClick={() => openUser()}>
              {t('settings.users.add')}
            </button>
          }
        >
          <DataTable
            rowKey={(u: User) => String(u.id)}
            rows={users.data ?? []}
            columns={[
              { key: 'name', header: t('settings.users.fields.name'), cell: (u) => <b>{u.full_name}</b> },
              { key: 'user', header: t('settings.users.fields.username'), cell: (u) => <span>{u.username}{u.must_change_password ? <> <Badge tone="amber">{t('settings.users.temporary')}</Badge></> : null}</span> },
              { key: 'email', header: t('settings.users.fields.email'), cell: (u) => u.email ?? '' },
              { key: 'role', header: t('settings.users.fields.role'), cell: (u) => t(`roles.${u.role_code}`, { defaultValue: u.role_name }) },
              { key: 'loc', header: t('settings.users.fields.locations'), cell: (u) => (u.role_scoped ? u.location_ids.map((id) => locationName.get(id) ?? id).join(', ') : t('settings.users.allLocations')) },
              { key: 'active', header: t('common.status'), cell: (u) => <Badge tone={u.active ? 'green' : 'red'}>{u.active ? t('common.active') : t('common.inactive')}</Badge> },
              {
                key: 'actions',
                header: '',
                cell: (u) => (
                  <div className="actions">
                    <button type="button" className="btn sm" onClick={() => openUser(u)}>
                      {t('common.edit')}
                    </button>
                    <button type="button" className="btn sm" onClick={() => setResetFor(u)}>
                      {t('settings.users.resetPassword')}
                    </button>
                  </div>
                ),
              },
            ]}
          />
        </Panel>

        <Panel title={t('settings.payments.title')} description={t('settings.payments.description')} badge={null}>
          <form className="actions" id="financierForm" onSubmit={(e) => void submitPm(e)}>
            <input className="input" id="pmCode" placeholder={t('settings.payments.codePlaceholder')} value={pm.code} onChange={(e) => setPm({ ...pm, code: e.target.value.toUpperCase() })} required />
            <input className="input" id="pmName" placeholder={t('settings.payments.namePlaceholder')} value={pm.name} onChange={(e) => setPm({ ...pm, name: e.target.value })} required style={{ flex: 1 }} />
            <select className="select" id="pmKind" value={pm.kind} onChange={(e) => setPm({ ...pm, kind: e.target.value as (typeof KINDS)[number] })}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`paymentKind.${k}`)}
                </option>
              ))}
            </select>
            <button className="btn black" type="submit" disabled={busy}>
              {t('settings.payments.add')}
            </button>
          </form>
          <div id="financierList" style={{ marginTop: 10 }}>
            {(methods.data ?? []).map((m) => (
              <div className="alert" key={m.id} data-payment={m.code}>
                <div>
                  <b>{m.name}</b>
                  <small>
                    {m.code} · {t(`paymentKind.${m.kind}`)}
                    {m.collected ? '' : ` · ${t('settings.payments.notCollected')}`}
                  </small>
                </div>
                <div className="actions">
                  <Badge tone={m.active ? 'green' : 'red'}>{m.active ? t('common.active') : t('common.inactive')}</Badge>
                  <button type="button" className="btn sm" onClick={() => void togglePm(m)} disabled={busy}>
                    {m.active ? t('common.deactivate') : t('common.activate')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="panel" style={{ marginTop: 14 }}>
        <div className="head">
          <div>
            <h3>{t('settings.movementTypes.title')}</h3>
            <p>{t('settings.movementTypes.description')}</p>
          </div>
        </div>
        <DataTable
          rowKey={(m: { code: string }) => m.code}
          rows={movementTypes.data ?? []}
          columns={[
            { key: 'code', header: t('settings.movementTypes.code'), cell: (m) => <b>{m.code}</b> },
            { key: 'name', header: t('settings.movementTypes.name'), cell: (m) => t(`movementType.${m.code}`, { defaultValue: m.name }) },
            { key: 'dir', header: t('settings.movementTypes.direction'), cell: (m) => t(`settings.movementTypes.dir.${m.direction}`) },
            { key: 'rev', header: t('settings.movementTypes.reversible'), cell: (m) => (m.reversible ? t('common.yes') : t('common.no')) },
          ]}
        />
      </div>

      <Modal open={userForm !== null} title={userForm?.id ? t('settings.users.editTitle') : t('settings.users.newTitle')} help={t('settings.users.modalHelp')} onClose={() => setUserForm(null)}>
        {userForm ? (
          <form className="formgrid" id="userForm" onSubmit={(e) => void submitUser(e)}>
            <div className="field span2">
              <label htmlFor="uName">{t('settings.users.fields.name')}</label>
              <input id="uName" required value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="uUsername">{t('settings.users.fields.username')}</label>
              <input id="uUsername" required={!userForm.id} disabled={!!userForm.id} pattern="[a-z0-9][a-z0-9._\-]{1,39}" autoCapitalize="none" autoComplete="off" title={t('settings.users.usernameHelp')} placeholder={t('settings.users.usernamePlaceholder')} value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value.toLowerCase() })} />
              {!userForm.id ? <div className="help">{t('settings.users.usernameHelp')}</div> : null}
            </div>
            <div className="field">
              <label htmlFor="uEmail">{t('settings.users.fields.email')}</label>
              <input id="uEmail" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
            </div>
            {!userForm.id ? (
              <div className="field span2">
                <label htmlFor="uPassword">{t('settings.users.fields.password')}</label>
                <input id="uPassword" type="password" required minLength={8} autoComplete="new-password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} />
                <div className="help">{t('settings.users.temporaryHelp')}</div>
              </div>
            ) : null}
            <div className="field">
              <label htmlFor="uRole">{t('settings.users.fields.role')}</label>
              <select id="uRole" value={userForm.role_code} onChange={(e) => setUserForm({ ...userForm, role_code: e.target.value })}>
                {(roles.data ?? []).map((r) => (
                  <option key={r.code} value={r.code}>
                    {t(`roles.${r.code}`, { defaultValue: r.name })}
                  </option>
                ))}
              </select>
            </div>
            {!userForm.id ? (
              <div className="field">
                <label htmlFor="uLang">{t('settings.users.fields.language')}</label>
                <select id="uLang" value={userForm.preferred_language} onChange={(e) => setUserForm({ ...userForm, preferred_language: e.target.value as Language })}>
                  <option value="es">{t('languages.es')}</option>
                  <option value="en">{t('languages.en')}</option>
                </select>
              </div>
            ) : (
              <div className="field">
                <label htmlFor="uActive">{t('common.status')}</label>
                <select id="uActive" value={userForm.active ? '1' : '0'} onChange={(e) => setUserForm({ ...userForm, active: e.target.value === '1' })}>
                  <option value="1">{t('common.active')}</option>
                  <option value="0">{t('common.inactive')}</option>
                </select>
              </div>
            )}
            {roleScoped(userForm.role_code) ? (
              <div className="field span2">
                <label>{t('settings.users.fields.locations')}</label>
                <div className="checklist" id="uLocations">
                  {(locations.data ?? [])
                    .filter((l) => l.active)
                    .map((l) => (
                      <label key={l.id} className="check">
                        <input type="checkbox" checked={userForm.location_ids.includes(l.id)} onChange={() => toggleLocation(l.id)} /> {l.name}
                      </label>
                    ))}
                </div>
                <div className="help">{t('settings.users.locationsHelp')}</div>
              </div>
            ) : (
              <div className="v52-note span2">{t('settings.users.unscopedHelp')}</div>
            )}
            {userForm.id ? (
              <div className="field span2">
                <label>{t('settings.users.fields.modules')}</label>
                <div className="checklist" id="uModules">
                  {MODULES.map((m) => {
                    const delRol = VIEW_PERMISSION[m].some((p) => (roles.data ?? []).find((r) => r.code === userForm.role_code)?.permissions.includes(p));
                    const marcado = VIEW_PERMISSION[m].some((p) => userForm.permissions.includes(p));
                    return (
                      <label key={m} className="check" data-module={m}>
                        <input type="checkbox" checked={marcado} onChange={() => toggleModule(VIEW_PERMISSION[m])} aria-label={t(`nav.${m}`)} />
                        {' '}{t(`nav.${m}`)}
                        {marcado !== delRol ? <span className="tiny muted"> · {t('settings.users.moduleException')}</span> : null}
                      </label>
                    );
                  })}
                </div>
                <div className="help">{t('settings.users.modulesHelp')}</div>
              </div>
            ) : (
              <div className="v52-note span2">{t('settings.users.modulesNew')}</div>
            )}
            <button className="btn black span2" type="submit" disabled={busy}>
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </form>
        ) : null}
      </Modal>

      <Modal open={resetFor !== null} title={t('settings.users.resetTitle', { name: resetFor?.full_name ?? '' })} onClose={() => setResetFor(null)}>
        <form className="formgrid" onSubmit={(e) => void submitReset(e)}>
          <div className="field span2">
            <label htmlFor="rpNew">{t('settings.users.fields.newPassword')}</label>
            <input id="rpNew" type="password" required minLength={8} autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <div className="help">{t('settings.users.temporaryHelp')}</div>
          </div>
          <button className="btn black span2" type="submit" disabled={busy}>
            {t('common.save')}
          </button>
        </form>
      </Modal>
    </>
  );
}
